import { Info, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocale } from "../contexts/LocaleContext";
import { api } from "../lib/api";
import { formatDimensionText, formatProductSize } from "../lib/productDisplay";
import { SearchSpecBadges } from "./search/SearchSpecBadges";

const escape = value => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const looksLikeArticlePrefix = value => {
  const normalized = value.trim().toUpperCase();
  if (/^(?:DIN|ISO|EN|NF)[-_/]?\d/.test(normalized) || /^M\d+(?:[.,]\d+)?(?:[X-]\d+)?$/.test(normalized)) return false;
  return /^[A-Z0-9][A-Z0-9._/-]{3,}$/.test(normalized) && /[A-Z]/.test(normalized) && /\d/.test(normalized);
};

const Highlight = ({ text = "", query }) => {
  const compact = query.trim();
  if (!compact) return text;
  return String(text).split(new RegExp(`(${escape(compact)})`, "ig")).map((part, index) => part.toLowerCase() === compact.toLowerCase() ? <mark key={index}>{part}</mark> : part);
};

export function SearchAutocomplete({ onNavigate, variant = "header", placeholder, buttonLabel, buttonIcon }) {
  const { locale, t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const root = useRef(null);
  const inputRef = useRef(null);
  const controller = useRef(null);
  const activeQuery = useRef("");
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState([]);
  const [eventId, setEventId] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [intent, setIntent] = useState(null);
  const [interactive, setInteractive] = useState(false);
  const hero = variant === "hero" || variant === "buyer";
  const mobileHeader = variant === "mobileHeader";
  const prefix = variant === "buyer" ? "buyer" : (hero ? "hero" : (mobileHeader ? "mobile-header" : "header"));
  const suggestionsId = `${prefix}-search-suggestions`;
  const testId = value => variant === "buyer" ? `buyer-${value}` : (hero ? `hero-${value}` : (mobileHeader ? `mobile-header-${value}` : value));
  const items = groups.flatMap(group => group.items.map(item => ({ ...item, group: group.key })));

  useEffect(() => {
    if (location.pathname === "/search") { setInteractive(false); setQuery(new URLSearchParams(location.search).get("q") || ""); }
  }, [location.pathname, location.search]);
  useEffect(() => {
    const close = event => { if (root.current && !root.current.contains(event.target)) { setInteractive(false); setOpen(false); } };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);
  useEffect(() => {
    controller.current?.abort();
    const term = query.trim();
    if (!interactive) { setLoading(false); return undefined; }
    if (term.length < 2) { setGroups([]); setOpen(false); setLoading(false); return undefined; }
    const timer = setTimeout(async () => {
      const request = new AbortController();
      const requestKey = `${term}|${locale}`;
      controller.current = request; activeQuery.current = requestKey; setLoading(true);
      const isCurrent = () => !request.signal.aborted && activeQuery.current === requestKey;
      try {
        if (looksLikeArticlePrefix(term)) {
          const { data } = await api.get("/search/article-prefix", { params: { q: term, limit: 50 }, signal: request.signal });
          if (isCurrent()) {
            setGroups(data.results?.length ? [{ key: "article_numbers", label: t("product.articleNumber"), items: data.results }] : []);
            setEventId(null); setIntent({ query_route: data.query_route, identity_confidence: data.identity_confidence });
            setOpen(document.activeElement === inputRef.current); setActive(-1);
          }
        } else {
          const { data } = await api.get("/search/suggestions", { params: { q: term, locale }, signal: request.signal });
          if (isCurrent()) {
            setGroups(data.groups || []); setEventId(data.event_id); setIntent(data.intent || null);
            setOpen(document.activeElement === inputRef.current); setActive(-1);
          }
        }
      } catch (error) {
        if (error.code !== "ERR_CANCELED" && isCurrent()) { setGroups([]); setOpen(false); }
      } finally {
        if (isCurrent()) setLoading(false);
      }
    }, 200);
    return () => { clearTimeout(timer); controller.current?.abort(); };
  }, [query, locale, t, interactive]);

  const choose = (item, index) => {
    if (eventId) api.post("/search/events/click", { event_id: eventId, article_id: item.article_id, position: index + 1, result_score: item.score, bucket: item.bucket || "exact" }).catch(() => {});
    setInteractive(false); setOpen(false); onNavigate?.(); navigate(item.canonical_path);
  };
  const submit = event => {
    event.preventDefault(); const term = query.trim(); if (!term) return;
    setInteractive(false); setOpen(false); onNavigate?.(); navigate(`/search?q=${encodeURIComponent(term)}`);
  };
  const paste = event => {
    const value = event.clipboardData?.getData("text") || "";
    const bulk = value.includes("\n") || value.includes("\t") || (value.match(/\b\d+\s*(?:pcs?|stuks?|stk|ea)\b/gi) || []).length > 1;
    if (!bulk) return;
    event.preventDefault(); sessionStorage.setItem("aramfix-rfq-draft", value); setOpen(false); onNavigate?.(); navigate("/rfq?bulk=1");
  };
  const keyDown = event => {
    if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); setActive(current => Math.min(items.length - 1, current + 1)); }
    else if (event.key === "ArrowUp") { event.preventDefault(); setActive(current => Math.max(-1, current - 1)); }
    else if (event.key === "Enter" && active >= 0) { event.preventDefault(); choose(items[active], active); }
    else if (event.key === "Escape") { setOpen(false); setActive(-1); }
  };
  const removeConstraint = constraint => {
    const patterns = {
      standard: /\b(?:DIN|ISO|EN|NEN|ANSI|ASME)\s*-?\s*\d+(?:[-/]\d+)*\b/i,
      diameter: /\bM\s*-?\s*\d+(?:[.,]\d+)?\b/i,
      length: /(?:[x×]\s*)?\d+(?:[.,]\d+)?\s*(?:mm|cm)?\b/i,
      grade: /\b(?:class|klasse|grade)?\s*(?:A[24](?:\s*-?\s*\d{2})?|\d{1,2}[.,]\d)\b/i,
    };
    const pattern = patterns[constraint.key] || new RegExp(escape(constraint.matched_text || constraint.value), "i");
    setQuery(current => current.replace(pattern, " ").replace(/\s+/g, " ").trim());
  };

  return <div className={`search-autocomplete ${hero ? "hero-product-search" : "header-product-search"}`} ref={root} data-testid={`${prefix}-product-search`}>
    <form className={hero ? "hero-search" : "header-search"} onSubmit={submit} data-testid={`${prefix}-search-form`} role="search">
      <Search/><input ref={inputRef} value={query} onChange={event => { setInteractive(true); setQuery(event.target.value); }} onPaste={paste} onFocus={() => { setInteractive(true); if (groups.length) setOpen(true); }} onKeyDown={keyDown} placeholder={placeholder || t("nav.searchPlaceholder")} aria-label={placeholder || t("nav.searchPlaceholder")} aria-autocomplete="list" aria-expanded={open} aria-controls={suggestionsId} aria-activedescendant={active >= 0 ? `${prefix}-search-suggestion-${active}` : undefined} role="combobox" autoComplete="off" data-testid={`${prefix}-search-input`}/>
      <button type="submit" data-testid={variant === "buyer" ? "buyer-search-button" : (hero ? "hero-search-button" : (mobileHeader ? "mobile-header-search-submit" : "header-search-submit"))}>{buttonLabel || t("nav.search")}{buttonIcon}</button>
    </form>
    {open && <div className="search-suggestions" id={suggestionsId} role="listbox" data-testid={testId("search-suggestions")}>
      {intent?.constraints?.length > 0 && <div className="search-autocomplete-chips" data-testid={testId("search-autocomplete-constraints")}>{intent.constraints.map((constraint, index) => <button type="button" key={`${constraint.field}-${constraint.value}-${index}`} onClick={() => removeConstraint(constraint)} data-testid={testId(`search-autocomplete-remove-${constraint.field}-${index}`)}><Info/>{constraint.label}: {constraint.value}<X/></button>)}</div>}
      {loading && <p className="search-suggestion-state" data-testid={testId("search-suggestions-loading")}>…</p>}
      {!loading && !items.length && <button type="button" className="search-suggestion-state" onClick={() => navigate(`/search?q=${encodeURIComponent(query.trim())}`)} data-testid={testId("search-no-suggestions")}>{t("nav.search")}</button>}
      {groups.map(group => <section key={group.key} data-testid={testId(`search-suggestion-group-${group.key}`)}>
        <h3>{group.label || t("nav.products")}</h3>
        {group.items.map(item => {
          const index = items.findIndex(row => row.article_id === item.article_id);
          return <button type="button" id={`${prefix}-search-suggestion-${index}`} role="option" aria-selected={index === active} className={index === active ? "active" : ""} onMouseEnter={() => setActive(index)} onClick={() => choose(item, index)} key={`${group.key}-${item.article_id}`} data-testid={testId(`search-suggestion-${item.sku}`)}>
            <span className="search-suggestion-image">{item.image_url ? <img src={item.image_url} alt=""/> : <Search/>}</span>
            <span className="search-suggestion-main"><strong><Highlight text={formatDimensionText(item.name)} query={query}/></strong><small><Highlight text={item.sku} query={query}/></small><em>{[item.standard, formatProductSize(item.diameter, item.length), item.material_grade, item.surface, item.packing].filter(Boolean).join(" · ")}</em><SearchSpecBadges item={{ ...item, field_comparison: (item.field_comparison || item.field_checks || []).slice(0, 3) }}/></span>
            <span className="search-suggestion-reason">{item.match_reason}{item.stock_indicator === "in_stock" && <i>{t("stock.inStock")}</i>}</span>
          </button>;
        })}
      </section>)}
    </div>}
  </div>;
}